import React, { useEffect, useRef, useState } from 'react';
import { localToday } from '../../utils/dateUtils';
import { escapeHtml } from '../../utils/escapeHtml';
import Webcam from 'react-webcam';
import './internos.css';
import { useUsers } from '../../context/usersContext';
import { useInternals } from '../../context/internalContext';
import { useUnits } from '../../context/unitsContext';
import { useAuxiliaries } from '../../context/auxiliariesContext';
import ConfirmationModal from '../../components/modal/confirmationModal';
import FormattedInput from '../../components/FormatdInput/formatFunctions';
import TooltipInfo from '../../components/TooltipInfo';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import { locationIdFromTypedText, locationLabel, validateLocationSelection } from '../../utils/locationSelection';
import { toast } from 'react-toastify';
import useSubmitLock from '../../utils/useSubmitLock';
import {
  FaIdCard, FaUserPlus, FaUndo, FaProcedures, FaGavel,
  FaCamera, FaSearch, FaMapMarkerAlt, FaCheckCircle, FaArrowLeft, FaArrowRight,
} from 'react-icons/fa';
import { FaHandcuffs } from 'react-icons/fa6';
import { ENTRY_TYPE_CONFIG } from './entryTypeConfig';

// Ícone por código de tipo de entrada, só para deixar os cartões de seleção mais legíveis.
// Tipo sem ícone mapeado aqui cai no ícone padrão (FaIdCard) — não precisa mexer aqui pra
// adicionar um tipo novo, é só cosmético (ver entryTypeConfig.js pro registro que importa de verdade).
const ENTRY_TYPE_ICONS = {
  entrada_sistema: FaUserPlus,
  retorno_fulga: FaHandcuffs,
  retorno_saida_temporaria: FaUndo,
  retorno_internacao_externa: FaProcedures,
  prisao_em_saida_temporaria: FaGavel,
};

// Fluxo novo e único de entrada, dirigido pelo "Tipo de Entrada" (TypeEntry) do backend.
// Convive por enquanto com o cadastro antigo (registerInternal.js) e com os botões de retorno
// por linha em index.js/internalsByUnit.js — nenhum dos dois foi alterado. Este arquivo é
// independente: não importa nada de registerInternal.js.

const initialInclusionData = {
  name: '',
  nickname: '',
  age: '',
  naturalness_city: '',
  cpf: '',
  pregnancy: false,
  pregnancy_time: '',
  health_situation: '',
  full_uniform: false,
  observation_uniforms: '',
  hygiene_kit: false,
  observation_hygiene_kit: '',
  clothing: '',
  documents_collected: false,
  observation_documents: '',
  other_items_collected: false,
  observation_other_items: '',
  others: '',
  inclusion_date: '',
};

const booleanFields = new Set([
  'pregnancy',
  'full_uniform',
  'hygiene_kit',
  'documents_collected',
  'other_items_collected',
]);

const normalizedTextFields = new Set([
  'name',
  'nickname',
  'pregnancy_time',
  'health_situation',
  'observation_uniforms',
  'observation_hygiene_kit',
  'clothing',
  'others',
  'observation_documents',
  'observation_other_items',
]);

const initialEntradaData = {
  name: '',
  mothers_name: '',
  cpf: '',
  date_of_birth: '',
  nationality_country: '',
  naturalness_city: '',
  cell: '',
  start_date: '',
};

const normalizeUpperNoAccent = (value) => {
  if (!value) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
};

const calculateAge = (birthDate) => {
  if (!birthDate) return '';
  const date = new Date(birthDate);
  if (Number.isNaN(date.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) age -= 1;
  return age;
};

const normalizeCollectionPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const getPavilionUnitId = (pavilion) => Number(
  pavilion?.unit?.id ?? pavilion?.unit_id ?? pavilion?.unit
);
const getCellPavilionId = (cell) => Number(
  cell?.pavilion?.id ?? cell?.pavilion_id ?? cell?.pavilion
);

// Confere se o status atual do interno encontrado está entre os status exigidos pelo TypeEntry
// selecionado (`current_status_names`, vindo direto do backend). Se o tipo não declarar nenhum
// status exigido, não bloqueia.
const isInternalStatusAllowedForType = (internal, type) => {
  const requiredStatuses = type?.current_status_names;
  if (!Array.isArray(requiredStatuses) || requiredStatuses.length === 0) return true;
  const statusName = internal?.status?.name || internal?.status;
  return requiredStatuses.some(
    (name) => normalizeUpperNoAccent(name) === normalizeUpperNoAccent(statusName)
  );
};

// Motivo (texto) pra travar a seleção de um interno já no modal de busca, antes mesmo de deixar
// clicar — em vez de só bloquear depois, no submit. Vazio quando o interno pode ser selecionado.
const getEntrySelectionBlockReason = (internal, type) => {
  if (isInternalStatusAllowedForType(internal, type)) return '';
  const statusName = internal?.status?.name || internal?.status || 'desconhecido';
  const required = Array.isArray(type?.current_status_names) ? type.current_status_names.join(', ') : '';
  return `Status atual: ${statusName}. Não é possível dar entrada${required ? ` — exige: ${required}` : ''}.`;
};

function UnifiedEntry({ onSave, onLockChange }) {
  const {
    listTypeEntry,
    createEntryRecord,
    searchInternal,
    listInternals,
    listInternalConduct,
    getInternalById,
    listInternalInclusion,
    createInternalInclusion,
    updateInternalInclusion,
    getLastPendingTemporaryLeavingRecord,
  } = useInternals();
  const { listCells, listPavillions, getCellById } = useUnits();
  const { effectiveUnitId, requireWriteUnit } = useUsers();
  const { listCountry, listCity } = useAuxiliaries();

  useEffect(() => {
    if (typeof onLockChange === 'function') onLockChange(false);
  }, [onLockChange]);

  // --- Tipo de entrada ---
  const [typeEntryOptions, setTypeEntryOptions] = useState([]);
  const [selectedTypeCode, setSelectedTypeCode] = useState('');
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);

  useEffect(() => {
    const loadTypeEntries = async () => {
      setIsLoadingTypes(true);
      const response = await listTypeEntry({ active: true });
      const results = normalizeCollectionPayload(response);
      // Ordem na tela segue a ordem das chaves em ENTRY_TYPE_CONFIG, não a ordem que o backend
      // devolve — assim um tipo novo adicionado no final do config aparece por último nos cartões.
      const configOrder = Object.keys(ENTRY_TYPE_CONFIG);
      const supported = results
        .filter((type) => Object.prototype.hasOwnProperty.call(ENTRY_TYPE_CONFIG, type?.code))
        .sort((a, b) => configOrder.indexOf(a.code) - configOrder.indexOf(b.code));
      setTypeEntryOptions(supported);
      setIsLoadingTypes(false);
    };
    loadTypeEntries();
  }, [listTypeEntry]);

  const selectedType = typeEntryOptions.find((type) => type.code === selectedTypeCode) || null;
  const selectedVariant = selectedTypeCode ? ENTRY_TYPE_CONFIG[selectedTypeCode]?.formVariant : null;

  // ================= Retorno simples (fuga / saída temporária / internação médica) =================
  // Nem todo interno tem CPF cadastrado, então a busca aceita CPF OU nome. Por CPF (11 dígitos)
  // o resultado já vem certeiro; por nome pode vir mais de um interno, e aí mostramos uma lista
  // pra confirmar visualmente (mesmo componente ConfirmationModal usado no wizard de cadastro).
  const [retornoCpf, setRetornoCpf] = useState('');
  const [retornoName, setRetornoName] = useState('');
  const [retornoFoundInternal, setRetornoFoundInternal] = useState(null);
  const [retornoSearchAttempted, setRetornoSearchAttempted] = useState(false);
  const [retornoSearchResults, setRetornoSearchResults] = useState([]);
  const [isRetornoSearchModalOpen, setIsRetornoSearchModalOpen] = useState(false);
  const [isSearchingRetorno, setIsSearchingRetorno] = useState(false);
  const [retornoPavilion, setRetornoPavilion] = useState('');
  const [retornoCell, setRetornoCell] = useState('');
  const [retornoPavilions, setRetornoPavilions] = useState([]);
  const [retornoCells, setRetornoCells] = useState([]);
  const [isLoadingRetornoPavilions, setIsLoadingRetornoPavilions] = useState(false);
  const [isLoadingRetornoCells, setIsLoadingRetornoCells] = useState(false);
  // Campos extras que só alguns tipos de retorno pedem (ver `extraFields` em entryTypeConfig.js) —
  // hoje só o retorno de fuga, com os mesmos campos que o antigo endpoint `recapture` já pedia.
  const [retornoConduct, setRetornoConduct] = useState('');
  const [retornoConducts, setRetornoConducts] = useState([]);
  const [isLoadingRetornoConducts, setIsLoadingRetornoConducts] = useState(false);
  const [retornoLimitDays, setRetornoLimitDays] = useState('');
  const [retornoReturnDate, setRetornoReturnDate] = useState(localToday());
  const { isSubmitting: isSubmittingRetorno, runWithSubmitLock: runRetornoSubmit } = useSubmitLock();
  const retornoExtraFields = ENTRY_TYPE_CONFIG[selectedTypeCode]?.extraFields || [];

  const resetRetornoState = () => {
    setRetornoCpf('');
    setRetornoName('');
    setRetornoFoundInternal(null);
    setRetornoSearchAttempted(false);
    setRetornoSearchResults([]);
    setIsRetornoSearchModalOpen(false);
    setRetornoPavilion('');
    setRetornoCell('');
    setRetornoPavilions([]);
    setRetornoCells([]);
    setRetornoConduct('');
    setRetornoConducts([]);
    setRetornoLimitDays('');
    setRetornoReturnDate(localToday());
  };

  useEffect(() => {
    if (selectedVariant !== 'retorno-simples') return undefined;
    const cpfDigits = retornoCpf.replace(/\D/g, '');
    const nameQuery = retornoName.trim();

    if (cpfDigits.length !== 11 && nameQuery.length < 3) {
      setRetornoFoundInternal(null);
      setRetornoSearchAttempted(false);
      setRetornoSearchResults([]);
      return undefined;
    }

    const delay = setTimeout(async () => {
      setIsSearchingRetorno(true);
      try {
        if (cpfDigits.length === 11) {
          const result = await searchInternal({ cpf: cpfDigits });
          const found = result?.cpf?.data && Object.keys(result.cpf.data).length > 0
            ? result.cpf.data
            : null;
          setRetornoFoundInternal(found);
          setRetornoSearchResults([]);
        } else {
          // Busca por nome de interno já cadastrado: usa a mesma listagem paginada da tela
          // de Internos (internals/internal/), não o `searchInternal` (esse é específico do
          // fluxo de cadastro/entrada nova e só funciona bem com CPF ou nome+mãe+nascimento
          // juntos). Filtra já pelo status exigido pelo tipo de entrada, quando só há um.
          const statusFilter = selectedType?.current_status_names?.length === 1
            ? selectedType.current_status_names[0]
            : undefined;
          const response = await listInternals({
            name: nameQuery,
            limit: 20,
            ...(statusFilter ? { status: statusFilter } : {}),
          });
          const list = Array.isArray(response?.results) ? response.results : [];
          setRetornoSearchResults(list);
          setRetornoFoundInternal(null);
          if (list.length > 0) setIsRetornoSearchModalOpen(true);
        }
        setRetornoSearchAttempted(true);
      } finally {
        setIsSearchingRetorno(false);
      }
    }, 500);

    return () => clearTimeout(delay);
  }, [retornoCpf, retornoName, selectedVariant, searchInternal, listInternals, selectedType]);

  const handleSelectRetornoInternal = (internal) => {
    setRetornoFoundInternal(internal);
    setIsRetornoSearchModalOpen(false);
  };

  useEffect(() => {
    if (!retornoFoundInternal) return;
    const writeUnit = requireWriteUnit();
    if (!writeUnit.ok) {
      toast.warning(writeUnit.message);
      return;
    }

    let isMounted = true;
    const loadPavilions = async () => {
      setIsLoadingRetornoPavilions(true);
      try {
        const response = await listPavillions({ unit: writeUnit.unitId });
        const allPavilions = normalizeCollectionPayload(response);
        const filtered = allPavilions.filter(
          (pavilion) => String(getPavilionUnitId(pavilion)) === String(writeUnit.unitId)
        );
        if (!isMounted) return;
        setRetornoPavilions(filtered);

        // Alguns tipos (retorno/prisão em saída temporária) pré-carregam pavilhão/cela com a
        // cela de origem de onde o interno saiu — igual o antigo botão de ícone na tabela já
        // fazia (handleOpenTemporaryReturnModal). Só entra se o tipo declarar isso em
        // entryTypeConfig.js e se a cela de origem pertencer a um pavilhão da unidade atual.
        if (ENTRY_TYPE_CONFIG[selectedTypeCode]?.prefillOriginCell) {
          try {
            const activeRecord = await getLastPendingTemporaryLeavingRecord(retornoFoundInternal.id);
            const originCellId = activeRecord?.origin_cell;
            if (!originCellId) return;

            const originCell = await getCellById(originCellId, { silent: true });
            const originPavilionId = getCellPavilionId(originCell);
            const prefilledPavilionId = originPavilionId ? String(originPavilionId) : '';
            const hasPrefilledPavilion = filtered.some(
              (pavilion) => String(pavilion.id) === prefilledPavilionId
            );
            if (!isMounted || !hasPrefilledPavilion) return;

            setRetornoPavilion(prefilledPavilionId);
            setIsLoadingRetornoCells(true);
            try {
              const cellsResponse = await listCells({ pavilion: prefilledPavilionId });
              const allCells = normalizeCollectionPayload(cellsResponse);
              const filteredCells = allCells.filter(
                (cell) => String(getCellPavilionId(cell)) === prefilledPavilionId
              );
              if (!isMounted) return;
              setRetornoCells(filteredCells);
              const prefilledCellId = String(originCellId);
              const hasPrefilledCell = filteredCells.some(
                (cell) => String(cell.id) === prefilledCellId
              );
              if (hasPrefilledCell) setRetornoCell(prefilledCellId);
            } finally {
              if (isMounted) setIsLoadingRetornoCells(false);
            }
          } catch (error) {
            console.error('Erro ao pré-carregar cela de origem:', error);
          }
        }
      } finally {
        if (isMounted) setIsLoadingRetornoPavilions(false);
      }
    };
    loadPavilions();
    return () => { isMounted = false; };
  }, [
    retornoFoundInternal,
    requireWriteUnit,
    listPavillions,
    selectedTypeCode,
    getLastPendingTemporaryLeavingRecord,
    getCellById,
    listCells,
  ]);

  useEffect(() => {
    const needsConduct = (ENTRY_TYPE_CONFIG[selectedTypeCode]?.extraFields || []).includes('conduct');
    if (!retornoFoundInternal || !needsConduct) return undefined;
    let isMounted = true;
    const loadConducts = async () => {
      setIsLoadingRetornoConducts(true);
      try {
        const response = await listInternalConduct();
        if (isMounted) setRetornoConducts(response?.results || []);
      } catch (error) {
        console.error('Erro ao carregar condutas:', error);
      } finally {
        if (isMounted) setIsLoadingRetornoConducts(false);
      }
    };
    loadConducts();
    return () => { isMounted = false; };
  }, [retornoFoundInternal, selectedTypeCode, listInternalConduct]);

  const handleRetornoPavilionChange = async (event) => {
    const pavilionId = event.target.value;
    setRetornoPavilion(pavilionId);
    setRetornoCell('');
    setRetornoCells([]);
    if (!pavilionId) return;

    setIsLoadingRetornoCells(true);
    try {
      const response = await listCells({ pavilion: pavilionId });
      const allCells = normalizeCollectionPayload(response);
      setRetornoCells(allCells.filter((cell) => String(getCellPavilionId(cell)) === String(pavilionId)));
    } catch (error) {
      toast.error('Erro ao carregar celas.');
    } finally {
      setIsLoadingRetornoCells(false);
    }
  };

  const handleRetornoSubmit = async (event) => {
    event.preventDefault();
    await runRetornoSubmit(async () => {
      if (!retornoFoundInternal?.id) {
        toast.warning('Busque e confirme o interno pelo CPF antes de continuar.');
        return;
      }
      if (!isInternalStatusAllowedForType(retornoFoundInternal, selectedType)) {
        toast.error(
          `Este tipo de entrada exige que o interno esteja com status: ${selectedType.current_status_names.join(', ')}.`
        );
        return;
      }
      if (!retornoCell) {
        toast.warning('Selecione uma cela.');
        return;
      }
      if (retornoExtraFields.includes('return_date') && !retornoReturnDate) {
        toast.warning('Informe a data do retorno.');
        return;
      }

      try {
        const body = {
          internal: retornoFoundInternal.id,
          type_entry: selectedType.id,
          cell: Number(retornoCell),
        };
        if (retornoExtraFields.includes('conduct')) {
          body.conduct = retornoConduct ? Number(retornoConduct) : 0;
        }
        if (retornoExtraFields.includes('limit_days')) {
          body.limit_days = retornoLimitDays ? Number(retornoLimitDays) : 0;
        }
        if (retornoExtraFields.includes('return_date')) {
          body.return_date = retornoReturnDate;
        }

        const response = await createEntryRecord(body);

        if (response) {
          toast.success(`${ENTRY_TYPE_CONFIG[selectedTypeCode]?.label || 'Entrada'} registrada com sucesso.`);
          resetRetornoState();
          setSelectedTypeCode('');
          if (typeof onSave === 'function') await onSave();
        }
      } catch (error) {
        const apiMessage = error?.response?.data?.message || error?.response?.data?.detail;
        toast.error(typeof apiMessage === 'string' && apiMessage ? apiMessage : 'Erro ao registrar entrada.');
      }
    });
  };

  // ================= Wizard de cadastro / nova entrada (entrada_sistema) =================
  const [entradaData, setEntradaData] = useState(initialEntradaData);
  const [entradaStep, setEntradaStep] = useState(1);
  const [entradaPavilion, setEntradaPavilion] = useState('');
  const [entradaPavilions, setEntradaPavilions] = useState([]);
  const [entradaCells, setEntradaCells] = useState([]);
  const [existingInternal, setExistingInternal] = useState(null);
  const [isExistingInternalConfirmed, setIsExistingInternalConfirmed] = useState(false);
  const [foundInternals, setFoundInternals] = useState([]);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isSearchingEntrada, setIsSearchingEntrada] = useState(false);
  const [manualSearch, setManualSearch] = useState(true);
  const { isSubmitting: isSubmittingEntrada, runWithSubmitLock: runEntradaSubmit } = useSubmitLock();

  // Foto de perfil (webcam + arquivo)
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const selectedProfileFileRef = useRef(null);
  const [showWebcam, setShowWebcam] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [hasSelectedProfilePhoto, setHasSelectedProfilePhoto] = useState(false);
  const [profilePreview, setProfilePreview] = useState('');

  // Sugestões de nacionalidade (lista completa, filtro local) e naturalidade (busca no backend)
  const [countryList, setCountryList] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [nationalityName, setNationalityName] = useState('');
  const [naturalnessName, setNaturalnessName] = useState('');
  const [shouldSearchCity, setShouldSearchCity] = useState(false);

  useEffect(() => {
    if (selectedVariant !== 'wizard-cadastro') return;
    const fetchCountries = async () => {
      try {
        const response = await listCountry();
        setCountryList(response?.results || []);
      } catch (error) {
        console.error('Erro ao buscar países:', error);
      }
    };
    fetchCountries();
  }, [selectedVariant, listCountry]);

  const handleCountrySearch = (query) => {
    setNationalityName(query);
    setEntradaData((prev) => ({ ...prev, nationality_country: locationIdFromTypedText(query) }));
    setFilteredCountries(
      query.trim() === ''
        ? []
        : countryList.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()))
    );
  };

  const selectCountry = (selectedCountry) => {
    setNationalityName(selectedCountry.name);
    setEntradaData((prev) => ({ ...prev, nationality_country: selectedCountry.id }));
    setFilteredCountries([]);
  };

  const handleCitySearch = (query) => {
    setNaturalnessName(query);
    setEntradaData((prev) => ({ ...prev, naturalness_city: locationIdFromTypedText(query) }));
    setShouldSearchCity(true);
    setCitySearchQuery(query);
  };

  const selectCity = (selectedCity) => {
    setNaturalnessName(selectedCity.name);
    setEntradaData((prev) => ({ ...prev, naturalness_city: selectedCity.id }));
    setFilteredCities([]);
    setShouldSearchCity(false);
    setCitySearchQuery(selectedCity.name);
  };

  useEffect(() => {
    const query = citySearchQuery.trim();
    if (!shouldSearchCity || query.length < 3) {
      setFilteredCities([]);
      return undefined;
    }
    const debounce = setTimeout(async () => {
      try {
        const response = await listCity({ name: query });
        setFilteredCities(response?.results || []);
      } catch (error) {
        console.error('Erro ao buscar cidades:', error);
        setFilteredCities([]);
      }
    }, 350);
    return () => clearTimeout(debounce);
  }, [citySearchQuery, shouldSearchCity, listCity]);

  // Ficha de inclusão + upload assinado
  const [internalId, setInternalId] = useState(null);
  const [inclusionId, setInclusionId] = useState(null);
  const [inclusionData, setInclusionData] = useState(initialInclusionData);
  const [inclusionNaturalnessName, setInclusionNaturalnessName] = useState('');
  const [inclusionFile, setInclusionFile] = useState(null);
  const [inclusionFilePreview, setInclusionFilePreview] = useState('');
  const [isFinalizingInclusion, setIsFinalizingInclusion] = useState(false);
  const [showInclusionWebcam, setShowInclusionWebcam] = useState(false);
  const [inclusionCameraError, setInclusionCameraError] = useState('');
  const inclusionWebcamRef = useRef(null);
  const inclusionFileInputRef = useRef(null);
  const inclusionCameraInputRef = useRef(null);

  useEffect(() => {
    if (selectedVariant !== 'wizard-cadastro') return undefined;
    if (effectiveUnitId === null || effectiveUnitId === undefined || String(effectiveUnitId) === '') {
      setEntradaPavilions([]);
      return undefined;
    }
    let isMounted = true;
    const fetchPavilions = async () => {
      const response = await listPavillions({ unit: effectiveUnitId });
      const allPavilions = normalizeCollectionPayload(response);
      const filtered = allPavilions.filter(
        (pavilion) => String(getPavilionUnitId(pavilion)) === String(effectiveUnitId)
      );
      if (isMounted) setEntradaPavilions(filtered);
    };
    fetchPavilions();
    return () => { isMounted = false; };
  }, [selectedVariant, effectiveUnitId, listPavillions]);

  const handleEntradaPavilionChange = async (event) => {
    const pavilionId = event.target.value;
    setEntradaPavilion(pavilionId);
    setEntradaData((prev) => ({ ...prev, cell: '' }));
    setEntradaCells([]);
    if (!pavilionId) return;

    const response = await listCells({ pavilion: Number(pavilionId) });
    const allCells = normalizeCollectionPayload(response);
    setEntradaCells(allCells.filter((cell) => getCellPavilionId(cell) === Number(pavilionId)));
  };

  const handleEntradaChange = (event) => {
    const { name, value } = event.target;
    setEntradaData((prev) => ({ ...prev, [name]: value }));
    if (name === 'cpf' && existingInternal) {
      // Interno confirmado deixou de ser válido porque o CPF foi alterado manualmente.
      setExistingInternal(null);
      setIsExistingInternalConfirmed(false);
      setManualSearch(true);
    }
  };

  // Busca automática por CPF (ou nome + mãe + nascimento, quando o CPF não é conhecido)
  useEffect(() => {
    if (selectedVariant !== 'wizard-cadastro' || !manualSearch) return undefined;

    const delay = setTimeout(async () => {
      const cpfDigits = (entradaData.cpf || '').replace(/\D/g, '');
      try {
        if (cpfDigits.length === 11) {
          setIsSearchingEntrada(true);
          const result = await searchInternal({ cpf: cpfDigits });
          const found = result?.cpf?.data && Object.keys(result.cpf.data).length > 0
            ? result.cpf.data
            : null;
          if (found) {
            setFoundInternals([found]);
            setIsSearchModalOpen(true);
          }
        } else if (!cpfDigits && entradaData.name && entradaData.mothers_name && entradaData.date_of_birth) {
          setIsSearchingEntrada(true);
          const result = await searchInternal({
            name: entradaData.name,
            mothers_name: entradaData.mothers_name,
            date_of_birth: entradaData.date_of_birth,
          });
          const data = result?.outher_fields?.data;
          const list = Array.isArray(data) ? data : (data && Object.keys(data).length > 0 ? [data] : []);
          if (list.length > 0) {
            setFoundInternals(list);
            setIsSearchModalOpen(true);
          }
        }
      } finally {
        setIsSearchingEntrada(false);
      }
    }, 500);

    return () => clearTimeout(delay);
  }, [
    entradaData.cpf,
    entradaData.name,
    entradaData.mothers_name,
    entradaData.date_of_birth,
    selectedVariant,
    manualSearch,
    searchInternal,
  ]);

  const handleSelectExistingInternal = (internal) => {
    setManualSearch(false);
    setIsExistingInternalConfirmed(true);
    setExistingInternal(internal);
    // O GET devolve o texto de país/cidade; o ID só existe quando o usuário escolhe na lista
    setNationalityName(locationLabel(internal.nationality_country));
    setNaturalnessName(locationLabel(internal.naturalness_city));
    setEntradaData((prev) => ({
      ...prev,
      name: internal.name || '',
      mothers_name: internal.mothers_name || '',
      cpf: internal.cpf || '',
      date_of_birth: internal.date_of_birth ? internal.date_of_birth.substring(0, 10) : '',
      nationality_country: '',
      naturalness_city: '',
      start_date: '',
    }));
    // Entrada nova sempre exige uma foto nova, mesmo que o interno já tenha uma cadastrada.
    setHasSelectedProfilePhoto(false);
    setProfilePreview('');
    selectedProfileFileRef.current = null;
    setIsSearchModalOpen(false);
  };

  const handleCloseSearchModal = () => setIsSearchModalOpen(false);

  const handleImageContainerClick = () => {
    if (showWebcam) return;
    setCameraError('');
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    setShowWebcam(true);
  };

  const captureFromWebcam = async () => {
    if (!webcamRef.current) return;
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) return;
    const res = await fetch(imageSrc);
    const blob = await res.blob();
    const file = new File([blob], 'perfil.jpg', { type: 'image/jpeg' });
    selectedProfileFileRef.current = file;
    setHasSelectedProfilePhoto(true);
    setProfilePreview(URL.createObjectURL(file));
    setShowWebcam(false);
  };

  const handleProfileFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      selectedProfileFileRef.current = null;
      setHasSelectedProfilePhoto(false);
      setProfilePreview('');
      return;
    }
    selectedProfileFileRef.current = file;
    setHasSelectedProfilePhoto(true);
    setProfilePreview(URL.createObjectURL(file));
    setShowWebcam(false);
    setCameraError('');
  };

  const isEmptyValue = (value) => value === null || value === undefined || String(value).trim() === '';

  const isEntradaFormValid = [
    entradaData.name,
    entradaData.mothers_name,
    entradaData.date_of_birth,
    entradaData.start_date,
    entradaPavilion,
    entradaData.cell,
  ].every((value) => !isEmptyValue(value)) && hasSelectedProfilePhoto;

  const handleEntradaSubmit = async (event) => {
    event.preventDefault();
    if (!isEntradaFormValid) {
      toast.error('Preencha todos os campos obrigatórios e selecione a foto de perfil.');
      return;
    }
    // País e cidade só existem no formulário de cadastro
    if (selectedVariant === 'wizard-cadastro' && !validateLocationSelection(entradaData)) return;
    if (existingInternal && !isInternalStatusAllowedForType(existingInternal, selectedType)) {
      toast.error(
        `Este tipo de entrada exige que o interno esteja com status: ${selectedType.current_status_names.join(', ')}.`
      );
      return;
    }
    if (existingInternal && !isExistingInternalConfirmed) {
      toast.error('Selecione o interno no modal de resultados para confirmar a entrada.');
      return;
    }

    await runEntradaSubmit(async () => {
      const fd = new FormData();
      fd.append('type_entry', selectedType.id);
      if (existingInternal?.id) fd.append('internal', existingInternal.id);
      fd.append('name', entradaData.name);
      fd.append('mothers_name', entradaData.mothers_name);
      const cpfDigits = (entradaData.cpf || '').replace(/\D/g, '');
      if (cpfDigits) fd.append('cpf', cpfDigits);
      fd.append('date_of_birth', entradaData.date_of_birth);
      if (entradaData.nationality_country) fd.append('nationality_country', entradaData.nationality_country);
      if (entradaData.naturalness_city) fd.append('naturalness_city', entradaData.naturalness_city);
      fd.append('cell', entradaData.cell);
      fd.append('start_date', entradaData.start_date);
      fd.append('image', selectedProfileFileRef.current);
      // A API de reconhecimento facial ainda não está funcionando. `entrada_sistema` é o único
      // TypeEntry com `recognition: true` hoje, então forçamos `check: false` (mesmo campo/valor
      // já usado em createInternal/entryInternal para pular a sincronização de reconhecimento)
      // pra não travar a entrada por causa da integração indisponível.
      fd.append('check', 'false');

      try {
        const response = await createEntryRecord(fd);
        if (!response) return;

        // Quando o interno já existia, já temos o id de quando ele foi encontrado/confirmado —
        // não depende do que a API devolve no `internal`.
        let resolvedInternalId = existingInternal?.id || response?.internal || null;

        // Cadastro novo: a resposta do entryrecord nem sempre traz `internal` preenchido pro
        // interno recém-criado. Faz uma busca de recuperação (mesma lógica usada na detecção de
        // interno existente) pra achar o id antes de desistir.
        if (!resolvedInternalId) {
          if (cpfDigits) {
            const searchResult = await searchInternal({ cpf: cpfDigits });
            resolvedInternalId = searchResult?.cpf?.data?.id || null;
          }
          if (!resolvedInternalId && entradaData.name && entradaData.mothers_name && entradaData.date_of_birth) {
            const searchResult = await searchInternal({
              name: entradaData.name,
              mothers_name: entradaData.mothers_name,
              date_of_birth: entradaData.date_of_birth,
            });
            const data = searchResult?.outher_fields?.data;
            const found = Array.isArray(data) ? data[0] : data;
            resolvedInternalId = found?.id || null;
          }
        }

        if (!resolvedInternalId) {
          toast.error('Entrada registrada, mas não foi possível identificar o interno para continuar.');
          return;
        }

        toast.success('Entrada registrada com sucesso.');
        setInclusionData((prev) => ({ ...prev, naturalness_city: entradaData.naturalness_city || prev.naturalness_city }));
        setInclusionNaturalnessName((prev) => naturalnessName || prev);
        setInternalId(resolvedInternalId);
        setEntradaStep(2);
      } catch (error) {
        const apiMessage = error?.response?.data?.message || error?.response?.data?.detail;
        toast.error(typeof apiMessage === 'string' && apiMessage ? apiMessage : 'Erro ao registrar entrada.');
      }
    });
  };

  // Pré-carrega a ficha de inclusão anterior (se existir) ou dados básicos do interno
  useEffect(() => {
    if (!internalId) return undefined;
    let isMounted = true;
    const loadInclusion = async () => {
      try {
        const data = await getInternalById(internalId);
        if (!data || !isMounted) return;

        const cpfDigits = String(data.cpf || '').replace(/\D/g, '');
        const inclusionResponse = cpfDigits ? await listInternalInclusion({ cpf: cpfDigits }) : null;
        const list = normalizeCollectionPayload(inclusionResponse).filter(
          (item) => String(item?.cpf || '').replace(/\D/g, '') === cpfDigits
        );
        const latest = [...list].sort((a, b) => {
          const aDate = new Date(a.updated_at || a.created_at || a.inclusion_date || 0).getTime();
          const bDate = new Date(b.updated_at || b.created_at || b.inclusion_date || 0).getTime();
          return bDate - aDate;
        })[0] || null;

        if (!isMounted) return;

        if (latest) {
          setInclusionId(latest.id || null);
          // O GET devolve o texto da cidade; o ID só existe quando o usuário escolhe na lista
          setInclusionData((prev) => ({
            ...prev,
            ...latest,
            naturalness_city: prev.naturalness_city,
            inclusion_date: latest.inclusion_date || localToday(),
          }));
          setInclusionNaturalnessName((prev) => locationLabel(latest.naturalness_city) || locationLabel(latest.naturalness) || prev);
          return;
        }

        setInclusionData((prev) => ({
          ...prev,
          name: normalizeUpperNoAccent(data.name || prev.name),
          cpf: data.cpf || prev.cpf,
          age: calculateAge(data.date_of_birth) || prev.age,
          inclusion_date: localToday(),
        }));
        setInclusionNaturalnessName((prev) => locationLabel(data.naturalness_city) || prev);
      } catch (error) {
        console.error('Erro ao carregar ficha de inclusão:', error);
      }
    };
    loadInclusion();
    return () => { isMounted = false; };
  }, [internalId, getInternalById, listInternalInclusion]);

  const handleInclusionChange = (event) => {
    const { name, value } = event.target;
    if (name === 'cpf') {
      setInclusionData((prev) => ({ ...prev, cpf: value.replace(/\D/g, '').slice(0, 11) }));
      return;
    }
    const nextValue = booleanFields.has(name)
      ? value === 'true'
      : normalizedTextFields.has(name)
        ? normalizeUpperNoAccent(value)
        : value;
    setInclusionData((prev) => ({ ...prev, [name]: nextValue }));
  };

  const handleInclusionNaturalnessChange = (text) => {
    setInclusionNaturalnessName(text);
    setInclusionData((prev) => ({ ...prev, naturalness_city: locationIdFromTypedText(text) }));
  };

  const handleInclusionNaturalnessSelect = (city) => {
    setInclusionNaturalnessName(city.name);
    setInclusionData((prev) => ({ ...prev, naturalness_city: city.id }));
  };

  const handleGenerateInclusionPdf = () => {
    if (!internalId) {
      toast.error('Nenhum interno selecionado.');
      return;
    }
    const pdfWindow = window.open('', '_blank');
    if (!pdfWindow) {
      toast.error('Permita pop-ups para gerar o PDF.');
      return;
    }

    // Todo valor vai pro HTML escapado: o PDF é aberto com document.write na origem do sistema.
    const toPdfText = (value) => (value === null || value === undefined || value === '' ? '-' : String(value));
    const fv = (value) => escapeHtml(toPdfText(value));
    const bl = (value) => (value ? 'Sim' : 'Não');
    const uv = (value) => escapeHtml(normalizeUpperNoAccent(toPdfText(value)));

    const html = `<html><head><title>Ficha de Inclusão</title>
      <style>
        @page { size: A4 portrait; margin: 8mm; }
        body { font-family: Arial, sans-serif; padding: 8px; color: #1f2937; font-size: 10px; }
        h1 { font-size: 15px; text-align: center; text-transform: uppercase; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        td { padding: 4px 6px; border: 1px solid #e5e7eb; font-size: 10px; }
        td.label { background: #f3f4f6; font-weight: 600; width: 35%; text-transform: uppercase; }
        .section { margin-top: 10px; font-weight: 700; text-transform: uppercase; background: #f3f4f6; padding: 4px 6px; border: 1px solid #e5e7eb; }
        .signature { margin-top: 32px; text-align: center; }
        .signature-line { width: 70%; margin: 0 auto 4px; border-bottom: 1px solid #111827; }
      </style></head><body>
      <h1>Ficha de Inclusão de Interno</h1>
      <div class="section">Identificação</div>
      <table>
        <tr><td class="label">Nome</td><td>${uv(inclusionData.name)}</td></tr>
        <tr><td class="label">Vulgo</td><td>${uv(inclusionData.nickname)}</td></tr>
        <tr><td class="label">CPF</td><td>${fv(inclusionData.cpf)}</td></tr>
        <tr><td class="label">Idade</td><td>${fv(inclusionData.age)}</td></tr>
        <tr><td class="label">Naturalidade</td><td>${uv(inclusionNaturalnessName)}</td></tr>
      </table>
      <div class="section">Estado de saúde</div>
      <table>
        <tr><td class="label">Gravidez</td><td>${bl(inclusionData.pregnancy)}</td></tr>
        <tr><td class="label">Tempo de gravidez</td><td>${uv(inclusionData.pregnancy_time)}</td></tr>
        <tr><td class="label">Outras enfermidades / situação</td><td>${uv(inclusionData.health_situation)}</td></tr>
      </table>
      <div class="section">Material entregue</div>
      <table>
        <tr><td class="label">Fardamento completo</td><td>${bl(inclusionData.full_uniform)}</td></tr>
        <tr><td class="label">Observação uniforme</td><td>${uv(inclusionData.observation_uniforms)}</td></tr>
        <tr><td class="label">Kit higiene/limpeza</td><td>${bl(inclusionData.hygiene_kit)}</td></tr>
        <tr><td class="label">Observação kit higiene</td><td>${uv(inclusionData.observation_hygiene_kit)}</td></tr>
      </table>
      <div class="section">Itens recolhidos</div>
      <table>
        <tr><td class="label">Itens recolhidos (joias, documentos etc.)</td><td>${bl(inclusionData.documents_collected)}</td></tr>
        <tr><td class="label">Observação documentos</td><td>${uv(inclusionData.observation_documents)}</td></tr>
        <tr><td class="label">Vestuário</td><td>${uv(inclusionData.clothing)}</td></tr>
        <tr><td class="label">Outros itens recolhidos</td><td>${bl(inclusionData.other_items_collected)}</td></tr>
        <tr><td class="label">Observação outros itens</td><td>${uv(inclusionData.observation_other_items)}</td></tr>
        <tr><td class="label">Outros</td><td>${uv(inclusionData.others)}</td></tr>
      </table>
      <table><tr><td class="label">Data</td><td>${fv(inclusionData.inclusion_date)}</td></tr></table>
      <div class="signature"><div class="signature-line"></div><div>Assinatura do Interno (ciente)</div><div>${uv(inclusionData.name)}</div></div>
      </body></html>`;

    pdfWindow.document.write(html);
    pdfWindow.document.close();
    pdfWindow.focus();
    pdfWindow.print();
    setEntradaStep(3);
  };

  const handleInclusionFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setInclusionFile(file);
    setShowInclusionWebcam(false);
    setInclusionCameraError('');
    setInclusionFilePreview(file?.type?.startsWith('image/') ? URL.createObjectURL(file) : '');
  };

  const captureInclusionFromWebcam = async () => {
    if (!inclusionWebcamRef.current) return;
    const imageSrc = inclusionWebcamRef.current.getScreenshot();
    if (!imageSrc) return;
    const res = await fetch(imageSrc);
    const blob = await res.blob();
    const file = new File([blob], 'ficha_inclusao_assinada.jpg', { type: 'image/jpeg' });
    setInclusionFile(file);
    setInclusionFilePreview(URL.createObjectURL(file));
    setShowInclusionWebcam(false);
  };

  const resetAll = () => {
    setSelectedTypeCode('');
    setEntradaStep(1);
    setEntradaData(initialEntradaData);
    setEntradaPavilion('');
    setEntradaCells([]);
    setExistingInternal(null);
    setIsExistingInternalConfirmed(false);
    setFoundInternals([]);
    setManualSearch(true);
    setHasSelectedProfilePhoto(false);
    setProfilePreview('');
    selectedProfileFileRef.current = null;
    setInternalId(null);
    setInclusionId(null);
    setInclusionData(initialInclusionData);
    setInclusionNaturalnessName('');
    setNationalityName('');
    setNaturalnessName('');
    setInclusionFile(null);
    setInclusionFilePreview('');
    setFilteredCountries([]);
    setFilteredCities([]);
    setCitySearchQuery('');
    setShouldSearchCity(false);
    resetRetornoState();
  };

  const handleFinalizeInclusion = async () => {
    if (isFinalizingInclusion) return;
    if (!internalId) {
      toast.error('Nenhum interno selecionado.');
      return;
    }
    if (!validateLocationSelection(inclusionData)) return;
    if (!inclusionFile) {
      toast.error('Envie o arquivo assinado.');
      return;
    }

    const writeUnit = requireWriteUnit();
    if (!writeUnit.ok) {
      toast.warning(writeUnit.message);
      return;
    }

    const fd = new FormData();
    fd.append('internal', internalId);
    fd.append('unit', writeUnit.unitId);
    Object.entries(inclusionData).forEach(([key, value]) => {
      if (key === 'cpf') {
        const cpfDigits = String(value || '').replace(/\D/g, '');
        if (cpfDigits) fd.append('cpf', `"${cpfDigits}"`);
        return;
      }
      if (booleanFields.has(key)) {
        fd.append(key, value ? 'true' : 'false');
        return;
      }
      if (key === 'age') {
        fd.append(key, value === '' || value === null || value === undefined ? '' : String(Number(value)));
        return;
      }
      if (key === 'naturalness_city') {
        // Só envia quando uma cidade foi escolhida na lista (ID)
        if (value) fd.append(key, String(value));
        return;
      }
      let nextValue = value;
      if (key === 'inclusion_date' && typeof nextValue === 'string' && nextValue.length > 10) {
        nextValue = nextValue.slice(0, 10);
      }
      fd.append(key, nextValue === null || nextValue === undefined ? '' : String(nextValue));
    });
    fd.append('file', inclusionFile);

    setIsFinalizingInclusion(true);
    try {
      const response = inclusionId
        ? await updateInternalInclusion(inclusionId, fd)
        : await createInternalInclusion(fd);
      if (!response) return;
      toast.success('Cadastro/entrada concluído com sucesso.');
      resetAll();
      if (typeof onSave === 'function') await onSave();
    } catch (error) {
      console.error('Erro ao salvar ficha de inclusão:', error);
    } finally {
      setIsFinalizingInclusion(false);
    }
  };

  const handleTypeSelect = (code) => {
    if (code === selectedTypeCode) return;
    resetAll();
    setSelectedTypeCode(code);
  };

  const getInitials = (name) => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  };

  const BoolToggle = ({ name, value, onChange, disabled }) => (
    <div className="unified-entry__toggle" role="group">
      <button
        type="button"
        className={!value ? 'is-active' : ''}
        disabled={disabled}
        onClick={() => onChange({ target: { name, value: 'false' } })}
      >
        Não
      </button>
      <button
        type="button"
        className={value ? 'is-active' : ''}
        disabled={disabled}
        onClick={() => onChange({ target: { name, value: 'true' } })}
      >
        Sim
      </button>
    </div>
  );

  return (
    <div className="unified-entry">
      <div className="unified-entry__header">
        <h2>Nova Entrada</h2>
        <p>Escolha o tipo de entrada para ver os campos necessários.</p>
      </div>

      <div className="unified-entry__types">
        {typeEntryOptions.map((type) => {
          const Icon = ENTRY_TYPE_ICONS[type.code] || FaIdCard;
          const isActive = type.code === selectedTypeCode;
          const description = ENTRY_TYPE_CONFIG[type.code]?.description;
          return (
            <button
              type="button"
              key={type.id}
              className={`unified-entry__type-card ${isActive ? 'is-active' : ''}`}
              onClick={() => handleTypeSelect(type.code)}
            >
              <span className="unified-entry__type-icon"><Icon /></span>
              <span className="unified-entry__type-name">{ENTRY_TYPE_CONFIG[type.code]?.label || type.name}</span>
              {description && (
                <span className="unified-entry__type-description">{description}</span>
              )}
            </button>
          );
        })}
        {isLoadingTypes && <p className="unified-entry__hint">Carregando tipos de entrada...</p>}
        {!isLoadingTypes && typeEntryOptions.length === 0 && (
          <p className="unified-entry__hint unified-entry__hint--error">Nenhum tipo de entrada disponível.</p>
        )}
      </div>

      {/* ================= Retorno simples ================= */}
      {selectedVariant === 'retorno-simples' && (
        <div className="unified-entry__panel">
          {Array.isArray(selectedType?.current_status_names) && selectedType.current_status_names.length > 0 && (
            <div className="unified-entry__notice">
              <FaMapMarkerAlt />
              <span>Requer que o interno esteja com status: <strong>{selectedType.current_status_names.join(', ')}</strong></span>
            </div>
          )}

          <div className="unified-entry__search">
            <FaSearch className="unified-entry__search-icon" />
            <FormattedInput
              type="cpf"
              name="cpf"
              maxLength={11}
              value={retornoCpf}
              onChange={(event) => setRetornoCpf(event.target.value)}
              placeholder="Digite o CPF do interno"
            />
            {isSearchingRetorno && <span className="unified-entry__spinner" />}
          </div>

          <p className="unified-entry__or">ou, se o interno não tiver CPF cadastrado</p>

          <div className="unified-entry__search">
            <FaSearch className="unified-entry__search-icon" />
            <input
              type="text"
              value={retornoName}
              onChange={(event) => setRetornoName(event.target.value)}
              placeholder="Busque pelo nome do interno"
              disabled={retornoCpf.replace(/\D/g, '').length === 11}
              autoComplete="off"
            />
          </div>

          {retornoSearchAttempted && !retornoFoundInternal && retornoSearchResults.length === 0 && (
            <p className="unified-entry__hint unified-entry__hint--error">Nenhum interno encontrado.</p>
          )}

          {retornoFoundInternal && (
            <form className="unified-entry__form" onSubmit={handleRetornoSubmit}>
              <div className="unified-entry__found">
                <span className="unified-entry__avatar">{getInitials(retornoFoundInternal.name)}</span>
                <div className="unified-entry__found-info">
                  <strong>{retornoFoundInternal.name}</strong>
                  <span>{retornoFoundInternal.status?.name || retornoFoundInternal.status}</span>
                </div>
                <FaCheckCircle className="unified-entry__found-check" />
              </div>

              <div className="unified-entry__grid">
                <div className="unified-entry__field">
                  <label>Pavilhão</label>
                  <select
                    value={retornoPavilion}
                    onChange={handleRetornoPavilionChange}
                    disabled={isLoadingRetornoPavilions || isSubmittingRetorno}
                    required
                  >
                    <option value="">
                      {isLoadingRetornoPavilions ? 'Carregando...' : 'Selecione o pavilhão'}
                    </option>
                    {retornoPavilions.map((pavilion) => (
                      <option key={pavilion.id} value={pavilion.id}>{pavilion.name}</option>
                    ))}
                  </select>
                </div>

                <div className="unified-entry__field">
                  <label>Cela</label>
                  <select
                    value={retornoCell}
                    onChange={(event) => setRetornoCell(event.target.value)}
                    disabled={!retornoPavilion || isLoadingRetornoCells || isSubmittingRetorno}
                    required
                  >
                    <option value="">
                      {isLoadingRetornoCells ? 'Carregando...' : 'Selecione a cela'}
                    </option>
                    {retornoCells.map((cell) => (
                      <option key={cell.id} value={cell.id}>{cell.name}</option>
                    ))}
                  </select>
                </div>

                {retornoExtraFields.includes('return_date') && (
                  <div className="unified-entry__field">
                    <label>Data do retorno<span className="unified-entry__required">*</span></label>
                    <input
                      type="date"
                      value={retornoReturnDate}
                      onChange={(event) => setRetornoReturnDate(event.target.value)}
                      max={localToday()}
                      required
                    />
                  </div>
                )}

                {retornoExtraFields.includes('conduct') && (
                  <div className="unified-entry__field">
                    <label>Conduta</label>
                    <select
                      value={retornoConduct}
                      onChange={(event) => setRetornoConduct(event.target.value)}
                      disabled={isLoadingRetornoConducts || isSubmittingRetorno}
                    >
                      <option value="">
                        {isLoadingRetornoConducts ? 'Carregando...' : 'Selecione a conduta'}
                      </option>
                      {retornoConducts.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {retornoExtraFields.includes('limit_days') && (
                  <div className="unified-entry__field">
                    <label>
                      Dias limite em cela
                      <TooltipInfo text="Quantidade de dias que o interno deve permanecer na cela após o retorno." position="top" />
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={retornoLimitDays}
                      onChange={(event) => setRetornoLimitDays(event.target.value)}
                      disabled={isSubmittingRetorno}
                    />
                  </div>
                )}
              </div>

              <div className="unified-entry__actions">
                <button type="submit" className="unified-entry__btn-primary" disabled={isSubmittingRetorno}>
                  {isSubmittingRetorno ? 'Salvando...' : 'Confirmar Entrada'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* ================= Wizard de cadastro / nova entrada ================= */}
      {selectedVariant === 'wizard-cadastro' && (
        <div className="unified-entry__panel">
          <div className="unified-entry__stepper">
            {[
              { n: 1, label: 'Dados da entrada' },
              { n: 2, label: 'Ficha de inclusão' },
              { n: 3, label: 'Upload assinado' },
            ].map((step, index) => (
              <React.Fragment key={step.n}>
                {index > 0 && <span className={`unified-entry__step-line ${entradaStep > index ? 'is-done' : ''}`} />}
                <div className={`unified-entry__step ${entradaStep === step.n ? 'is-active' : ''} ${entradaStep > step.n ? 'is-done' : ''}`}>
                  <span className="unified-entry__step-dot">{entradaStep > step.n ? <FaCheckCircle /> : step.n}</span>
                  <span className="unified-entry__step-label">{step.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>

          {entradaStep === 1 && (
            <form className="unified-entry__form" onSubmit={handleEntradaSubmit}>
              <div className="unified-entry__photo-row">
                <div
                  className={`unified-entry__photo ${showWebcam ? 'is-webcam' : ''}`}
                  onClick={handleImageContainerClick}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfileFileChange}
                    onClick={(event) => event.stopPropagation()}
                    style={{ display: 'none' }}
                  />
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleProfileFileChange}
                    onClick={(event) => event.stopPropagation()}
                    style={{ display: 'none' }}
                  />
                  {showWebcam ? (
                    <div className="unified-entry__webcam" onClick={(event) => event.stopPropagation()}>
                      <Webcam
                        ref={webcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{ facingMode: { ideal: 'environment' } }}
                        onUserMediaError={() => setCameraError('Não foi possível acessar a câmera. Verifique as permissões.')}
                      />
                      <div className="unified-entry__photo-actions">
                        <button type="button" className="unified-entry__btn-primary" onClick={captureFromWebcam}>Tirar foto</button>
                        <button type="button" className="unified-entry__btn-secondary" onClick={() => fileInputRef.current?.click()}>Arquivo</button>
                        <button type="button" className="unified-entry__btn-secondary" onClick={() => setShowWebcam(false)}>Cancelar</button>
                      </div>
                      {cameraError && <p className="unified-entry__hint unified-entry__hint--error">{cameraError}</p>}
                    </div>
                  ) : profilePreview ? (
                    <img src={profilePreview} alt="Imagem selecionada" />
                  ) : (
                    <div className="unified-entry__photo-placeholder">
                      <FaCamera />
                      <span>Foto de perfil</span>
                    </div>
                  )}
                </div>
                <p className="unified-entry__photo-hint">
                  Foto de perfil frontal<span className="unified-entry__required">*</span> — clique para tirar uma foto ou escolher um arquivo.
                </p>
              </div>

              <div className="unified-entry__section">
                <h3>Identificação</h3>
                <div className="unified-entry__grid">
                  <div className="unified-entry__field">
                    <label>CPF {isSearchingEntrada && <span className="unified-entry__spinner-inline" />}</label>
                    <FormattedInput type="cpf" name="cpf" maxLength={11} value={entradaData.cpf} onChange={handleEntradaChange} />
                  </div>

                  <div className="unified-entry__field">
                    <label>Nome<span className="unified-entry__required">*</span></label>
                    <FormattedInput type="nome" name="name" value={entradaData.name} onChange={handleEntradaChange} required />
                  </div>

                  <div className="unified-entry__field">
                    <label>Nome da mãe<span className="unified-entry__required">*</span></label>
                    <FormattedInput type="nome" name="mothers_name" value={entradaData.mothers_name} onChange={handleEntradaChange} required />
                  </div>

                  <div className="unified-entry__field">
                    <label>Data de nascimento<span className="unified-entry__required">*</span></label>
                    <input type="date" name="date_of_birth" value={entradaData.date_of_birth} onChange={handleEntradaChange} max={localToday()} required />
                  </div>

                  <div className="unified-entry__field unified-entry__field--autocomplete">
                    <label>Nacionalidade</label>
                    <input
                      type="text"
                      value={nationalityName}
                      onChange={(event) => handleCountrySearch(event.target.value)}
                      placeholder="Digite a nacionalidade"
                      autoComplete="off"
                    />
                    {filteredCountries.length > 0 && (
                      <ul className="unified-entry__suggestions">
                        {filteredCountries.map((c) => (
                          <li key={c.id} onClick={() => selectCountry(c)}>{c.name}</li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="unified-entry__field unified-entry__field--autocomplete">
                    <label>Naturalidade</label>
                    <input
                      type="text"
                      value={naturalnessName}
                      onChange={(event) => handleCitySearch(event.target.value)}
                      placeholder="Digite a naturalidade"
                      autoComplete="off"
                    />
                    {filteredCities.length > 0 && (
                      <ul className="unified-entry__suggestions">
                        {filteredCities.map((c) => (
                          <li key={c.id} onClick={() => selectCity(c)}>{c.name}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              <div className="unified-entry__section">
                <h3>Localização e data</h3>
                <div className="unified-entry__grid">
                  <div className="unified-entry__field">
                    <label>{existingInternal ? 'Data da entrada' : 'Data do cadastro/entrada'}<span className="unified-entry__required">*</span></label>
                    <input type="date" name="start_date" value={entradaData.start_date} onChange={handleEntradaChange} max={localToday()} required />
                  </div>

                  <div className="unified-entry__field">
                    <label>Pavilhão<span className="unified-entry__required">*</span></label>
                    <select value={entradaPavilion} onChange={handleEntradaPavilionChange} required>
                      <option value="">Selecione o pavilhão</option>
                      {entradaPavilions.map((pavilion) => (
                        <option key={pavilion.id} value={pavilion.id}>{pavilion.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="unified-entry__field">
                    <label>Cela<span className="unified-entry__required">*</span></label>
                    <select
                      name="cell"
                      value={entradaData.cell}
                      onChange={handleEntradaChange}
                      disabled={!entradaPavilion}
                      required
                    >
                      <option value="">Selecione a cela</option>
                      {entradaCells.map((cell) => (
                        <option key={cell.id} value={cell.id}>{cell.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="unified-entry__actions">
                <button type="submit" className="unified-entry__btn-primary" disabled={isSubmittingEntrada}>
                  {isSubmittingEntrada ? 'Salvando...' : (existingInternal ? 'Criar Entrada' : 'Salvar e continuar')}
                  {!isSubmittingEntrada && <FaArrowRight />}
                </button>
              </div>
            </form>
          )}

          {entradaStep === 2 && (
            <div className="unified-entry__form">
              <div className="unified-entry__section">
                <h3>Identificação</h3>
                <div className="unified-entry__grid">
                  <div className="unified-entry__field">
                    <label>Nome</label>
                    <FormattedInput type="nome" name="name" value={inclusionData.name} onChange={handleInclusionChange} />
                  </div>
                  <div className="unified-entry__field">
                    <label>Vulgo</label>
                    <input type="text" name="nickname" value={inclusionData.nickname} onChange={handleInclusionChange} />
                  </div>
                  <div className="unified-entry__field">
                    <label>Idade</label>
                    <input type="number" min="18" max="100" name="age" value={inclusionData.age} onChange={handleInclusionChange} />
                  </div>
                  <div className="unified-entry__field">
                    <label>Naturalidade</label>
                    <LocationAutocomplete
                      kind="city"
                      name="naturalness_city"
                      value={inclusionNaturalnessName}
                      onChange={handleInclusionNaturalnessChange}
                      onSelect={handleInclusionNaturalnessSelect}
                    />
                  </div>
                  <div className="unified-entry__field">
                    <label>CPF</label>
                    <FormattedInput type="cpf" name="cpf" value={inclusionData.cpf} onChange={handleInclusionChange} />
                  </div>
                </div>
              </div>

              <div className="unified-entry__section">
                <h3>Estado de saúde</h3>
                <div className="unified-entry__grid">
                  <div className="unified-entry__field">
                    <label>Gestante</label>
                    <BoolToggle name="pregnancy" value={inclusionData.pregnancy} onChange={handleInclusionChange} />
                  </div>
                  {inclusionData.pregnancy && (
                    <div className="unified-entry__field">
                      <label>Tempo de gravidez</label>
                      <input type="text" name="pregnancy_time" value={inclusionData.pregnancy_time} onChange={handleInclusionChange} />
                    </div>
                  )}
                  <div className="unified-entry__field unified-entry__field--full">
                    <label>Outras enfermidades / situação de saúde</label>
                    <textarea name="health_situation" value={inclusionData.health_situation} onChange={handleInclusionChange} />
                  </div>
                </div>
              </div>

              <div className="unified-entry__section">
                <h3>Material entregue</h3>
                <div className="unified-entry__grid">
                  <div className="unified-entry__field">
                    <label>Uniforme completo</label>
                    <BoolToggle name="full_uniform" value={inclusionData.full_uniform} onChange={handleInclusionChange} />
                  </div>
                  {!inclusionData.full_uniform && (
                    <div className="unified-entry__field">
                      <label>Observação uniforme</label>
                      <input type="text" name="observation_uniforms" value={inclusionData.observation_uniforms} onChange={handleInclusionChange} />
                    </div>
                  )}
                  <div className="unified-entry__field">
                    <label>Kit higiene</label>
                    <BoolToggle name="hygiene_kit" value={inclusionData.hygiene_kit} onChange={handleInclusionChange} />
                  </div>
                  {!inclusionData.hygiene_kit && (
                    <div className="unified-entry__field">
                      <label>Observação kit higiene</label>
                      <input type="text" name="observation_hygiene_kit" value={inclusionData.observation_hygiene_kit} onChange={handleInclusionChange} />
                    </div>
                  )}
                </div>
              </div>

              <div className="unified-entry__section">
                <h3>Itens recolhidos</h3>
                <div className="unified-entry__grid">
                  <div className="unified-entry__field">
                    <label>Vestuário</label>
                    <input type="text" name="clothing" value={inclusionData.clothing} onChange={handleInclusionChange} />
                  </div>
                  <div className="unified-entry__field">
                    <label>Documentos recolhidos</label>
                    <BoolToggle name="documents_collected" value={inclusionData.documents_collected} onChange={handleInclusionChange} />
                  </div>
                  {inclusionData.documents_collected && (
                    <div className="unified-entry__field">
                      <label>Observação documentos</label>
                      <input type="text" name="observation_documents" value={inclusionData.observation_documents} onChange={handleInclusionChange} />
                    </div>
                  )}
                  <div className="unified-entry__field">
                    <label>Outros itens recolhidos</label>
                    <BoolToggle name="other_items_collected" value={inclusionData.other_items_collected} onChange={handleInclusionChange} />
                  </div>
                  {inclusionData.other_items_collected && (
                    <div className="unified-entry__field">
                      <label>Observação outros itens</label>
                      <input type="text" name="observation_other_items" value={inclusionData.observation_other_items} onChange={handleInclusionChange} />
                    </div>
                  )}
                  <div className="unified-entry__field">
                    <label>Data de inclusão</label>
                    <input type="date" name="inclusion_date" value={inclusionData.inclusion_date} onChange={handleInclusionChange} />
                  </div>
                  <div className="unified-entry__field unified-entry__field--full">
                    <label>Observações gerais</label>
                    <textarea name="others" value={inclusionData.others} onChange={handleInclusionChange} />
                  </div>
                </div>
              </div>

              <div className="unified-entry__actions">
                <button type="button" className="unified-entry__btn-secondary" onClick={() => setEntradaStep(1)}>
                  <FaArrowLeft /> Voltar
                </button>
                <button type="button" className="unified-entry__btn-primary" onClick={handleGenerateInclusionPdf}>
                  Gerar PDF e avançar <FaArrowRight />
                </button>
              </div>
            </div>
          )}

          {entradaStep === 3 && (
            <div className="unified-entry__form">
              <div className="unified-entry__section">
                <h3>Arquivo da ficha assinada<span className="unified-entry__required">*</span></h3>
                <div
                  className="unified-entry__upload-zone"
                  onClick={() => !showInclusionWebcam && setShowInclusionWebcam(true)}
                >
                  <input
                    ref={inclusionFileInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={handleInclusionFileChange}
                    onClick={(event) => event.stopPropagation()}
                    style={{ display: 'none' }}
                  />
                  <input
                    ref={inclusionCameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleInclusionFileChange}
                    onClick={(event) => event.stopPropagation()}
                    style={{ display: 'none' }}
                  />
                  {showInclusionWebcam ? (
                    <div className="unified-entry__webcam" onClick={(event) => event.stopPropagation()}>
                      <Webcam
                        ref={inclusionWebcamRef}
                        screenshotFormat="image/jpeg"
                        videoConstraints={{ facingMode: { ideal: 'environment' } }}
                        onUserMediaError={() => setInclusionCameraError('Não foi possível acessar a câmera.')}
                      />
                      <div className="unified-entry__photo-actions">
                        <button type="button" className="unified-entry__btn-primary" onClick={captureInclusionFromWebcam}>Tirar foto</button>
                        <button type="button" className="unified-entry__btn-secondary" onClick={(event) => { event.stopPropagation(); inclusionFileInputRef.current?.click(); }}>Arquivo</button>
                        <button type="button" className="unified-entry__btn-secondary" onClick={() => setShowInclusionWebcam(false)}>Cancelar</button>
                      </div>
                      {inclusionCameraError && <p className="unified-entry__hint unified-entry__hint--error">{inclusionCameraError}</p>}
                    </div>
                  ) : inclusionFilePreview ? (
                    <img src={inclusionFilePreview} alt="Arquivo selecionado" />
                  ) : inclusionFile ? (
                    <div className="unified-entry__photo-placeholder">
                      <FaCheckCircle />
                      <span>{inclusionFile.name}</span>
                    </div>
                  ) : (
                    <div className="unified-entry__photo-placeholder">
                      <FaCamera />
                      <span>Clique para anexar a ficha assinada</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="unified-entry__actions">
                <button type="button" className="unified-entry__btn-secondary" onClick={() => setEntradaStep(2)} disabled={isFinalizingInclusion}>
                  <FaArrowLeft /> Voltar
                </button>
                <button type="button" className="unified-entry__btn-primary" onClick={handleFinalizeInclusion} disabled={isFinalizingInclusion}>
                  {isFinalizingInclusion ? 'Salvando...' : 'Salvar ficha'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={isSearchModalOpen}
        message="Interno já cadastrado. Selecione para confirmar a entrada:"
        internals={foundInternals}
        onSelectInternal={handleSelectExistingInternal}
        onClose={handleCloseSearchModal}
        getInternalDisabledReason={(internal) => getEntrySelectionBlockReason(internal, selectedType)}
      />

      <ConfirmationModal
        isOpen={isRetornoSearchModalOpen}
        message="Selecione o interno:"
        internals={retornoSearchResults}
        onSelectInternal={handleSelectRetornoInternal}
        onClose={() => setIsRetornoSearchModalOpen(false)}
        getInternalDisabledReason={(internal) => getEntrySelectionBlockReason(internal, selectedType)}
      />
    </div>
  );
}

export default UnifiedEntry;
